import { useEffect, useRef, useState } from "react";
import { Upload, FileText, Image as ImageIcon, Lock, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";

export function FileUploader({ endpoint, fields, accept, label, hint, onDone, testid = "file-uploader", privateFile = false }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const send = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File exceeds the 10MB limit");
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(privateFile ? "Evidence uploaded to private storage" : "Photo uploaded");
      onDone?.(data);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <label data-testid={testid} className={`flex items-center gap-3 border border-dashed rounded-md px-4 py-3 cursor-pointer transition-colors ${busy ? "opacity-60" : "hover:border-bordeaux"} ${privateFile ? "border-bordeaux/40 bg-bordeaux/5" : "border-[#D9D5CC] bg-white"}`}>
      {privateFile ? <Lock className="w-5 h-5 text-bordeaux shrink-0" /> : <ImageIcon className="w-5 h-5 text-gold shrink-0" />}
      <span className="flex-1 min-w-0"><span className="block text-sm font-medium">{busy ? "Uploading…" : label}</span>{hint && <span className="block text-[11px] text-[#6B675F]">{hint}</span>}</span>
      <Upload className="w-4 h-4 text-charcoal" />
      <input ref={ref} data-testid={`${testid}-input`} type="file" accept={accept} className="hidden" disabled={busy} onChange={(e) => send(e.target.files?.[0])} />
    </label>
  );
}

export function PrivateImage({ fileId, className = "" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/media/${fileId}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => setSrc(""));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileId]);
  if (src === null) return <div className={`skeleton ${className}`} />;
  if (!src) return <div className={`bg-stone flex items-center justify-center text-xs text-[#8A867E] ${className}`}>Preview unavailable</div>;
  return <img src={src} alt="" className={className} data-testid={`private-image-${fileId}`} />;
}

export function EvidenceList({ files = [], testid = "evidence-list" }) {
  const open = async (f) => {
    try {
      const { data } = await api.get(`/evidence/${f.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  if (!files.length) return <p className="text-[11px] text-[#8A867E]">No evidence files attached yet.</p>;
  return (
    <ul className="space-y-1" data-testid={testid}>
      {files.map((f) => (
        <li key={f.file_id} className="flex items-center gap-2 text-xs">
          <FileText className="w-3.5 h-3.5 text-bordeaux" /><span className="truncate flex-1">{f.name}</span><span className="text-[#8A867E]">{Math.round((f.size || 0) / 1024)} KB</span>
          <button data-testid={`evidence-view-${f.file_id}`} onClick={() => open(f)} className="inline-flex items-center gap-1 text-bordeaux font-medium hover:underline"><Eye className="w-3 h-3" /> View</button>
        </li>
      ))}
    </ul>
  );
}
